// 音乐文件流式URL工具函数
// 用于隐藏实际文件路径，统一使用流式传输端点

/**
 * 流式开关
 *
 * - 默认关闭（直连 https，可被浏览器显式访问，利于 CDN）
 * - 构建期：REACT_APP_USE_STREAM=1/true 才开启（需要重新 build）
 * - 运行期：仅在构建期已开启时，localStorage 可用于临时开关
 *   - 取值：'1'/'true' 开，'0'/'false' 关
 */
function isStreamEnabled() {
  try {
    const env = process.env.REACT_APP_USE_STREAM;
    const envEnabled = env === '1' || String(env).toLowerCase() === 'true';
    if (!envEnabled) return false;
  } catch (_) {}

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const v = window.localStorage.getItem('tenryu:use-stream');
      if (v === '0' || v === 'false') return false;
      if (v === '1' || v === 'true') return true;
    }
  } catch (_) {}

  // 构建期已启用时，默认走 stream
  return true;
}

// 供站内其他模块判断“全站 stream 是否启用”
export const isSiteStreamEnabled = () => isStreamEnabled();

function parseMetadata(music) {
  try {
    const raw = music?.metadata;
    return typeof raw === 'string' ? JSON.parse(raw) : (raw || null);
  } catch (_) {
    return null;
  }
}

function getDirAndBaseFromFilePath(music) {
  const raw = music?.file_path;
  if (!raw) return null;
  const normalized = String(raw).replace(/\\/g, '/');
  const parts = normalized.split('/');
  const file = parts.pop() || '';
  const base = file.replace(/\.[^/.]+$/, '');
  const dir = parts.join('/');
  if (!dir || !base) return null;
  return { dir, base };
}

function getDirectLyricsPath(music) {
  if (!music) return null;
  if (music.lyrics_file) return music.lyrics_file;
  const type = String(music.lyrics_type || '').trim().toLowerCase().replace(/^\./, '');
  const info = getDirAndBaseFromFilePath(music);
  if (!type || !info) return null;
  return `${info.dir}/${info.base}.${type}`;
}

function getDirectCoverPath(music) {
  if (!music) return null;
  if (music.cover_image) return music.cover_image;
  const info = getDirAndBaseFromFilePath(music);
  if (!info) return null;
  // 直连场景无法探测真实后缀，默认使用 webp（若无可自行上传或补齐）
  return `${info.dir}/${info.base}.webp`;
}

export const getDerivedVideoPath = (music) => {
  const meta = parseMetadata(music) || {};
  const ext = (meta?.video_ext || (meta?.video_file ? String(meta.video_file).split('.').pop() : '') || '').toLowerCase();
  if (!ext) return meta?.video_file || null;
  const info = getDirAndBaseFromFilePath(music);
  if (!info) return meta?.video_file || null;
  return `${info.dir}/${info.base}.${ext}`;
};

export const hasVideo = (music) => !!getDerivedVideoPath(music);

function pickDirectMediaPath(music, format = 'auto') {
  if (!music) return null;
  const meta = parseMetadata(music) || {};
  const convertedFormats = Array.isArray(meta?.convertedFormats) ? meta.convertedFormats : [];

  const byFmt = (fmt) => {
    const hit = convertedFormats.find((f) => String(f?.format || '').toLowerCase() === String(fmt).toLowerCase());
    return hit?.path || null;
  };

  if (format === 'video') return getDerivedVideoPath(music) || null;
  if (format === 'wav') return meta?.originalWavPath || null;
  if (format === 'flac') return byFmt('flac');
  if (format === 'opus') return byFmt('opus');
  if (format === 'mp3') return byFmt('mp3') || music.file_path || null;

  // auto：直接回退到主文件（避免在前端复制后端“会员/优先格式”逻辑）
  return music.file_path || byFmt('mp3') || byFmt('opus') || byFmt('flac') || null;
}

/**
 * 生成音乐文件的流式传输URL（隐藏实际文件路径）
 * @param {Object} music - 音乐对象
 * @param {string} format - 格式：'mp3', 'opus', 'flac', 'wav', 'video', 'auto'
 * @returns {string|null} 流式URL，如果music或id不存在则返回null
 */
export const getMusicStreamUrl = (music, format = 'auto') => {
  // 临时开关：关闭时直接引用文件路径（/musics/...）
  if (!isStreamEnabled()) {
    return pickDirectMediaPath(music, format);
  }

  if (!music || !music.id) return null;
  return `/api/music/${music.id}/stream${format !== 'auto' ? `?format=${format}` : ''}`;
};

/**
 * 生成歌词文件的流式传输URL（隐藏实际文件路径）
 * @param {Object} music - 音乐对象
 * @returns {string|null} 流式URL，如果music或id不存在则返回null
 */
export const getLyricsStreamUrl = (music) => {
  // 直连模式：尽量返回真实文件路径（/musics/...）
  if (!isStreamEnabled()) {
    return getDirectLyricsPath(music);
  }
  if (!music || !music.id) return null;
  // 统一流：歌词走 /stream（后端按同目录自动查找）
  return `/api/music/${music.id}/stream?asset=lyrics`;
};

/**
 * 生成封面图片的流式传输URL（封面与媒体同目录，可直接通过 stream 推导）
 * @param {Object} music - 音乐对象
 * @returns {string|null} 流式URL，如果music或id不存在则返回null
 */
export const getCoverStreamUrl = (music) => {
  // 直连模式：尽量返回真实文件路径（/musics/...）
  if (!isStreamEnabled()) {
    return getDirectCoverPath(music);
  }
  if (!music || !music.id) return null;
  // 统一流：封面也走 /stream（后端按同目录自动查找）
  return `/api/music/${music.id}/stream?asset=cover`;
};

/**
 * 获取封面图片URL（优先使用流式URL，回退到原始路径）
 * @param {Object} music - 音乐对象
 * @returns {string|null} 封面图片URL
 */
export const getCoverImageUrl = (music) => {
  if (!music) return null;
  // 直连模式优先返回 /musics/... 路径；流式模式走 /stream
  return getCoverStreamUrl(music) || music.cover_image || null;
};

export const hasLyrics = (music) => !!(music && music.lyrics_type);
